/**
 * QuickBookSlideOver.jsx · v2.7.45+E16 (Phase 6 · Feb-2026)
 * ─────────────────────────────────────────────────────────────────────
 * Global slide-over for one-key trip booking from anywhere in the
 * admin console. Triggered via Ctrl+B (or Cmd+B on macOS).
 *
 * Six fields → one dispatch CTA:
 *   • Rider name
 *   • Phone
 *   • Pickup address
 *   • Drop-off address
 *   • Passenger count
 *   • (optional) Note
 *
 * Posts to the SAME endpoint as the +Book Ride tab
 * (`POST /api/admin/booked-trips`) so the back-end stays a single
 * source of truth.
 *
 * Closed by default. The launcher button lives in AdminTopNav.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Zap, X, Loader2, MapPin, Phone, User, Users } from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";

const BLANK = { rider_name: "", rider_phone: "", origin: "", destination: "", passengers: 1, note: "" };
const ROLE_ALLOWED = new Set(["admin", "operating_partner", "developer", "supervisor", "dispatcher", "team_leader", "field_supervisor"]);

export default function QuickBookSlideOver({ open, onClose, adminRole }) {
  const [form, setForm] = useState(BLANK);
  const [submitting, setSubmitting] = useState(false);
  const firstRef = useRef(null);

  const reset = useCallback(() => setForm(BLANK), []);

  useEffect(() => {
    if (open) {
      // Focus first field after the slide animation
      setTimeout(() => firstRef.current?.focus(), 200);
    }
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!ROLE_ALLOWED.has(adminRole)) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.rider_name.trim() || !form.origin.trim() || !form.destination.trim()) {
      toast.error("Rider name + pickup + drop-off required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        rider_name:       form.rider_name.trim(),
        rider_phone:      form.rider_phone.trim() || null,
        origin_address:   form.origin.trim(),
        destination_address: form.destination.trim(),
        passengers:       Number(form.passengers) || 1,
        initial_note:     form.note.trim() || null,
        scheduled_for:    null,
        // these defaults let the backend compute the fare
        zone_id:          null,
        driver_id:        null,
      };
      const { data } = await adminApi.post("/admin/booked-trips", payload);
      toast.success(`Booking saved · #${data?.trip_id || data?.id || ""}`);
      reset();
      onClose?.();
    } catch (err) {
      toast.error(`Booking failed: ${err?.response?.data?.detail || err.message}`);
    } finally { setSubmitting(false); }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-black/40 transition-opacity ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Panel */}
      <aside
        className={`fixed top-0 right-0 bottom-0 z-[61] w-full sm:w-[420px] bg-white dark:bg-zinc-950 border-l border-slate-200 dark:border-zinc-800 shadow-2xl flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "translate-x-full"}`}
        data-testid="quick-book-slideover"
        aria-label="Quick book"
      >
        <header className="bg-amber-400 text-zinc-950 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5"/>
            <div>
              <div className="font-extrabold text-sm">Quick Book</div>
              <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">One-key dispatch · ⌘B / Ctrl+B</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="hover:bg-black/10 rounded-full p-1" aria-label="Close" data-testid="quick-book-close">
            <X className="h-4 w-4"/>
          </button>
        </header>

        <form onSubmit={submit} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="quick-book-form">
          <Row icon={User} label="Rider name *">
            <Input ref={firstRef} value={form.rider_name} onChange={(e) => update("rider_name", e.target.value)} placeholder="Jane Doe" className="text-sm" data-testid="quick-book-rider-name"/>
          </Row>
          <Row icon={Phone} label="Phone">
            <Input value={form.rider_phone} onChange={(e) => update("rider_phone", e.target.value)} placeholder="(405) 555-0188" className="text-sm" data-testid="quick-book-rider-phone"/>
          </Row>
          <Row icon={MapPin} label="Pickup *">
            <Input value={form.origin} onChange={(e) => update("origin", e.target.value)} placeholder="123 Main St, OKC" className="text-sm" data-testid="quick-book-origin"/>
          </Row>
          <Row icon={MapPin} label="Drop-off *">
            <Input value={form.destination} onChange={(e) => update("destination", e.target.value)} placeholder="OKC Airport (OKC)" className="text-sm" data-testid="quick-book-destination"/>
          </Row>
          <Row icon={Users} label="Passengers">
            <Input type="number" min={1} max={9} value={form.passengers} onChange={(e) => update("passengers", e.target.value)} className="text-sm w-24" data-testid="quick-book-passengers"/>
          </Row>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Note (optional)</label>
            <Textarea value={form.note} onChange={(e) => update("note", e.target.value)} rows={2} placeholder="Wheelchair · luggage · etc." className="text-sm mt-1" data-testid="quick-book-note"/>
          </div>
        </form>

        <footer className="border-t border-slate-200 dark:border-zinc-800 p-4 flex items-center justify-between gap-2 bg-slate-50 dark:bg-zinc-900/50">
          <div className="text-[10px] text-slate-500">
            <kbd className="bg-white dark:bg-zinc-900 border border-slate-300 dark:border-zinc-700 px-1.5 py-0.5 rounded text-[10px] font-mono">Esc</kbd> closes ·
            <kbd className="bg-white dark:bg-zinc-900 border border-slate-300 dark:border-zinc-700 px-1.5 py-0.5 rounded text-[10px] font-mono ml-1">⏎</kbd> dispatches
          </div>
          <Button onClick={submit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5" data-testid="quick-book-dispatch">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin"/> : <Zap className="h-4 w-4"/>}Dispatch
          </Button>
        </footer>
      </aside>
    </>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-3.5 w-3.5 text-slate-400 mt-1.5 flex-shrink-0"/>
      <div className="flex-1">
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1 block">{label}</label>
        {children}
      </div>
    </div>
  );
}
